import { useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/errors';
import { mapFieldErrors } from '@/api/fieldErrors';
import type { AffiliateCodeCreateRequest } from '@/api/generated';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';
import { SubmitButton } from '@/components/SubmitButton';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useFreshOnOpen } from '@/components/useFreshOnOpen';

const CODE_PATTERN = /^[A-Z0-9_-]{3,32}$/;

const FIELDS = ['code', 'label'] as const;

export interface NewCodeDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (body: AffiliateCodeCreateRequest) => Promise<void>;
}

export function NewCodeDialog(props: NewCodeDialogProps) {
  // A fresh form on every open (TMMRSL-0071).
  return <NewCodeForm key={useFreshOnOpen(props.open)} {...props} />;
}

function NewCodeForm({ open, onClose, onCreate }: NewCodeDialogProps) {
  const { t } = useTranslation();
  const describe = useActionError();
  const codeRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const fields = useFieldErrors(FIELDS, { code: codeRef, label: labelRef }, submitting);

  function handleClose() {
    if (submitting) return;
    onClose();
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = code.trim();
    if (!CODE_PATTERN.test(trimmed)) {
      setError(null);
      fields.fail({ code: t('validation.codePattern') });
      return;
    }
    setSubmitting(true);
    setError(null);
    fields.reset();
    try {
      const trimmedLabel = label.trim();
      const body: AffiliateCodeCreateRequest = {
        code: trimmed,
        ...(trimmedLabel.length > 0 ? { label: trimmedLabel } : {}),
      };
      await onCreate(body);
      onClose();
    } catch (err) {
      const action = describe(err);
      if (err instanceof ApiError && err.code === 'CONFLICT') {
        fields.fail({ code: t('affiliates.code.taken') });
      } else {
        setError(action);
        fields.failMarked(mapFieldErrors(action.fieldKeys, { code: 'code', label: 'label' }), {
          code: t('affiliates.code.codeHelp'),
          label: t('affiliates.field.label'),
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : handleClose} fullWidth maxWidth="sm">
      <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <DialogTitle>{t('affiliates.code.new')}</DialogTitle>
        <DialogContent>
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          <TextField
            required
            fullWidth
            margin="dense"
            label={t('affiliates.code.codeField')}
            inputRef={codeRef}
            helperText={fields.errors.code ?? t('affiliates.code.codeHelp')}
            error={fields.errors.code !== undefined}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              fields.clear('code');
            }}
            slotProps={{ htmlInput: { maxLength: 32 } }}
            disabled={submitting}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('affiliates.code.labelField')}
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
              fields.clear('label');
            }}
            inputRef={labelRef}
            error={fields.errors.label !== undefined}
            helperText={fields.errors.label}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={submitting}>
            {t('affiliates.code.create')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
